// Aplica los canales anatómicos (anatomy.js) al esqueleto del avatar (huesos estilo Mixamo/MetaPerson).
// Es la ÚNICA parte del pack que conoce los ejes de los huesos; todo lo demás habla en grados anatómicos.
//
// Calibración medida sobre avatar.glb (ver docs/MOTION_PACK.md). Convención de los huesos de este esqueleto:
//  - Pelvis/columna/cuello/cabeza: +X inclina hacia adelante, +Y gira hacia la IZQUIERDA del avatar, +Z inclina a la DERECHA.
//  - Muslo: +X = flexión (ambos lados); +Z = hacia la izquierda del avatar (abducción en la pierna izquierda, aducción en la derecha).
//  - Rodilla: flexión = -X. Tobillo: flexión plantar = +X.
//  - Hombro: X baja el brazo (90° - abducción); Y balancea el brazo adelante/atrás con signo opuesto en cada lado.
//  - Codo: flexión = +Z en el brazo izquierdo y -Z en el derecho (¡signos opuestos! con el mismo signo el derecho se dobla hacia atrás).
//  - Clavícula: +X la deprime (elevación = -X).
import * as THREE from 'three';
import { DEG, clamp } from './math.js';
import { romFor } from './anatomy.js';

const BONES = ['Hips', 'Spine', 'Spine1', 'Spine2', 'Neck', 'Head',
  'LeftShoulder', 'LeftArm', 'LeftForeArm', 'RightShoulder', 'RightArm', 'RightForeArm',
  'LeftUpLeg', 'LeftLeg', 'LeftFoot', 'LeftToeBase', 'RightUpLeg', 'RightLeg', 'RightFoot', 'RightToeBase'];

const SPINE_SPLIT = { Spine: 0.25, Spine1: 0.35, Spine2: 0.4 };
const FEET = ['LeftFoot', 'LeftToeBase', 'RightFoot', 'RightToeBase'];

const AX = { x: new THREE.Vector3(1, 0, 0), y: new THREE.Vector3(0, 1, 0), z: new THREE.Vector3(0, 0, 1) };
const qa = (axis, rad) => new THREE.Quaternion().setFromAxisAngle(AX[axis], rad);
const eul = (order, x, y, z) => new THREE.Quaternion().setFromEuler(new THREE.Euler(x * DEG, y * DEG, z * DEG, order));

/** Morph targets de cara (ARKit) por canal. */
function setMorph(meshes, name, v) {
  for (const m of meshes) {
    const i = m.morphTargetDictionary[name];
    if (i !== undefined) m.morphTargetInfluences[i] = v;
  }
}

export function bindRig(scene) {
  const bones = {};
  for (const n of BONES) {
    const o = scene.getObjectByName(n);
    if (o) bones[n] = { o, rest: o.quaternion.clone(), restPos: o.position.clone() };
  }
  const morphMeshes = [];
  scene.traverse((o) => { if (o.isMesh) o.frustumCulled = false; if (o.isSkinnedMesh && o.morphTargetDictionary) morphMeshes.push(o); });

  const root = bones.Hips ? bones.Hips.o.parent : scene;
  scene.updateMatrixWorld(true);
  const inv = new THREE.Matrix4();
  const v = new THREE.Vector3();
  const heightOf = (b) => { v.setFromMatrixPosition(b.o.matrixWorld).applyMatrix4(inv); return v.y; };
  inv.copy(root.matrixWorld).invert();
  const restFeet = FEET.filter((n) => bones[n]).map((n) => ({ n, y: heightOf(bones[n]) }));
  const parentScaleY = root.getWorldScale(new THREE.Vector3()).y || 1;

  const set = (name, q) => { const b = bones[name]; if (b) b.o.quaternion.copy(b.rest).multiply(q); };

  function arm(side, abd, flex) {
    const L = side === 'L';
    // flexión (balanceo adelante/atrás) por fuera, después la bajada del brazo
    set(L ? 'LeftArm' : 'RightArm', qa('y', (L ? -1 : 1) * flex * DEG).multiply(qa('x', (90 - abd) * DEG)));
  }

  /**
   * Aplica un diccionario de canales al esqueleto. Devuelve { clamped } con los canales que hubo que recortar al ROM
   * (para detectar clips que se pasan de rango).
   */
  function apply(ch) {
    const clamped = [];
    const get = (k) => {
      let x = ch[k] ?? 0;
      const r = romFor(k);
      if (r && (x < r[0] || x > r[1])) { clamped.push(k); x = clamp(x, r[0], r[1]); }
      return x;
    };

    // Pelvis (gira todo el cuerpo): rot + = derecha -> Y negativo; obl + = cae el lado derecho -> Z +
    set('Hips', eul('YXZ', get('pelvis.tilt'), -get('pelvis.rot'), get('pelvis.obl')));
    // Columna repartida en tres segmentos (relativa a la pelvis)
    for (const [n, w] of Object.entries(SPINE_SPLIT)) set(n, eul('YXZ', get('spine.flex') * w, -get('spine.rot') * w, get('spine.bend') * w));
    set('Neck', eul('YXZ', get('neck.flex'), -get('neck.rot'), get('neck.bend')));
    set('Head', eul('YXZ', get('head.flex'), -get('head.rot'), get('head.bend')));

    for (const s of ['L', 'R']) {
      const L = s === 'L';
      const P = L ? 'Left' : 'Right';
      arm(s, get(`${s}.shoulder.abd`), get(`${s}.shoulder.flex`));
      set(`${P}Shoulder`, qa('x', -get(`${s}.shoulder.elev`) * DEG));
      set(`${P}ForeArm`, qa('z', (L ? 1 : -1) * get(`${s}.elbow.flex`) * DEG));
      // Muslo: flexión por fuera de la abducción
      set(`${P}UpLeg`, eul('XZY', get(`${s}.hip.flex`), 0, (L ? 1 : -1) * get(`${s}.hip.abd`)));
      set(`${P}Leg`, qa('x', -get(`${s}.knee.flex`) * DEG));
      set(`${P}Foot`, qa('x', get(`${s}.ankle.pf`) * DEG));
    }

    // Desplazamiento de la pelvis: shift + = derecha (X local negativo), lift hacia arriba, advance hacia adelante
    const hips = bones.Hips;
    if (hips) {
      hips.o.position.set(hips.restPos.x - (ch['pelvis.shift'] ?? 0), hips.restPos.y + (ch['pelvis.lift'] ?? 0), hips.restPos.z + (ch['pelvis.advance'] ?? 0));
      // Contacto con el suelo: el pie más bajo nunca penetra ni flota respecto de la postura de reposo
      scene.updateMatrixWorld(true);
      inv.copy(root.matrixWorld).invert();
      let low = Infinity;
      for (const f of restFeet) low = Math.min(low, heightOf(bones[f.n]) - f.y);
      if (Number.isFinite(low)) hips.o.position.y -= low / parentScaleY;
    }

    // Cara (morph targets ARKit)
    const bl = clamp(get('face.blink'), 0, 1);
    setMorph(morphMeshes, 'eyeBlinkLeft', bl);
    setMorph(morphMeshes, 'eyeBlinkRight', bl);
    const sm = clamp(get('face.smile'), 0, 1);
    setMorph(morphMeshes, 'mouthSmileLeft', sm);
    setMorph(morphMeshes, 'mouthSmileRight', sm);
    const br = clamp(get('face.brow'), 0, 1);
    setMorph(morphMeshes, 'browInnerUp', br);
    setMorph(morphMeshes, 'browOuterUpLeft', br * 0.6);
    setMorph(morphMeshes, 'browOuterUpRight', br * 0.6);
    setMorph(morphMeshes, 'jawOpen', clamp(get('face.jaw'), 0, 1) * 0.6);
    setMorph(morphMeshes, 'mouthFunnel', clamp(get('face.funnel'), 0, 1));
    const gx = clamp(ch['gaze.x'] ?? 0, -1, 1);
    const gy = clamp(ch['gaze.y'] ?? 0, -1, 1);
    // mirada a la derecha del avatar: ojo derecho hacia afuera, izquierdo hacia adentro
    setMorph(morphMeshes, 'eyeLookOutRight', Math.max(0, gx));
    setMorph(morphMeshes, 'eyeLookInLeft', Math.max(0, gx));
    setMorph(morphMeshes, 'eyeLookOutLeft', Math.max(0, -gx));
    setMorph(morphMeshes, 'eyeLookInRight', Math.max(0, -gx));
    setMorph(morphMeshes, 'eyeLookUpLeft', Math.max(0, gy));
    setMorph(morphMeshes, 'eyeLookUpRight', Math.max(0, gy));
    setMorph(morphMeshes, 'eyeLookDownLeft', Math.max(0, -gy));
    setMorph(morphMeshes, 'eyeLookDownRight', Math.max(0, -gy));
    return { clamped };
  }

  return { apply, bones, morphMeshes };
}
